import { BoldText, DangerText, MuteText, SuccessText } from '@elements/Common';
import { DatepickerContainer, FormHelpToolTip } from '@elements/FormField';
import { Calendar, CaretDown, CaretRight, Tabs } from '@phosphor-icons/react';
import { CommonUtility } from '@utility/common';
import { ItemStates, tooltipTexts } from '@utility/constants/common';
import { DateFormat, DateUtility } from '@utility/date';
import { cn } from '@skupreme/shared/ui';
import {
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
} from 'react';
import { FIELD_INPUT } from '@elements/form-field/styles';
import { CenterDropdownStyle } from '@elements/Dropdown';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { ResponsiveDotts } from '@page-components/dashboard/common';
import DatePicker from 'react-datepicker';
import { StatusBox } from '@page-components/catalog';
import { useNavigate } from 'react-router';
import { CardTableRow, type CardTableRowProps } from '@components/CardTable';
import type { ShipmentCreateItem } from 'types/shipment-inbound/create';

export const viewTypes = {
  units: 'Units',
  cartons: 'Cartons',
};

type DivProps = HTMLAttributes<HTMLDivElement>;

export const Row = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:mb-[5px] sk:flex sk:items-center sk:justify-between sk:gap-x-[5px]',
        className,
      )}
    />
  ),
);
Row.displayName = 'Row';

export function PredicateSelectAll({ selectAllPredicateClick }) {
  return (
    <div className="sk:flex! sk:flex-col!">
      <span>Prediction (Units)</span>
      <BoldText
        className="sk:ml-1! sk:mt-1!"
        onClick={() => selectAllPredicateClick()}
      >
        <SuccessText>Select All</SuccessText>
      </BoldText>
    </div>
  );
}

export function PredicateDeSelectAll({ selectAllPredicateClick }) {
  return (
    <div className="sk:flex! sk:flex-col!">
      <span>Prediction (Units)</span>
      <BoldText
        className="sk:ml-1! sk:mt-1!"
        onClick={() => selectAllPredicateClick()}
      >
        <DangerText>Clear All</DangerText>
      </BoldText>
    </div>
  );
}

function CenterRow({ className, ...rest }: DivProps) {
  return <Row {...rest} className={cn('sk:justify-center', className)} />;
}

const humanizer = daysLeft =>
  DateUtility.yearMonthTime(
    new Date(),
    DateUtility.addDays(new Date(), daysLeft),
  );

export function UnitsAtDestination({
  present,
  reserved,
  expected,
  showDaysLeft,
  daysLeft,
}) {
  return (
    <>
      <CenterRow className="sk:justify-between!">
        <MuteText className="sk:mr-3.5! sk:flex!">
          Present
          <FormHelpToolTip helpText={tooltipTexts.present} />
        </MuteText>
        {present || 0}
      </CenterRow>
      <CenterRow className="sk:justify-between!">
        <MuteText className="sk:mr-3.5! sk:flex!">
          Reserved
          <FormHelpToolTip helpText={tooltipTexts.reserved} />
        </MuteText>
        {reserved || 0}
      </CenterRow>
      <CenterRow className="sk:justify-between!">
        <MuteText className="sk:mr-3.5! sk:flex!">
          Expected
          <FormHelpToolTip helpText={tooltipTexts.expected} />
        </MuteText>
        {expected || 0}
      </CenterRow>
      <CenterRow className="sk:justify-between!">
        <MuteText className="sk:mr-3.5! sk:flex!">Days Left</MuteText>
        {showDaysLeft && daysLeft !== null && (humanizer(daysLeft) || '0 Days')}
      </CenterRow>
    </>
  );
}

export function ExpandCollapseToggle({ expanded, onToggle }) {
  return (
    <div className="sk:flex! sk:flex-col! sk:items-start! sk:justify-center!">
      <MuteText>Multiple Aliases</MuteText>
      <div
        onClick={onToggle}
        className="sk:flex! sk:items-center! sk:cursor-pointer"
      >
        {expanded ? (
          <>
            <b>Collapse</b>
            <CaretDown className="sk:ml-1!" />
          </>
        ) : (
          <>
            <b>Expand</b>
            <CaretRight className="sk:ml-1!" />
          </>
        )}
      </div>
    </div>
  );
}

export function UnitsAtOrigin({
  totalCtns,
  showDaysLeft,
  daysLeft,
  isWSShipment = false,
  present,
}) {
  if (isWSShipment) {
    return (
      <div>
        <div className="sk:flex!">
          <MuteText>To Ship</MuteText>
          <span className="sk:ml-1!">{totalCtns}</span>
        </div>
        <div className="sk:flex!">
          <MuteText>At Origin</MuteText>
          <span className="sk:ml-1!">{present}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <span>
        {CommonUtility.numberWithCommas(CommonUtility.roundNumber(totalCtns))}
      </span>
      {showDaysLeft && !!daysLeft && (
        <CenterRow>
          <MuteText>
            ({!isNaN(daysLeft) ? humanizer(daysLeft) || '0 Days' : daysLeft})
          </MuteText>
        </CenterRow>
      )}
    </>
  );
}

export function ItemSku({ SkusList, skuValue, sku, onChange }) {
  if (SkusList?.length > 1) {
    return (
      <CenterDropdownStyle
        selection
        options={SkusList}
        value={skuValue}
        onChange={(e, { value }) => onChange(value)}
      />
    );
  }

  return (
    <ResponsiveDotts>
      <TextCopyToClipboard text={skuValue || sku} iconSize={16} />
    </ResponsiveDotts>
  );
}

export function ItemTags({
  isMissingInfo,
  isDiscontinued,
  isInactive,
  isDuplicateListing,
  state,
  sku,
}) {
  const navigate = useNavigate();
  const redirectToMissingInfo = e => {
    e?.stopPropagation();
    e?.preventDefault();

    const url = `/app/catalogs/${
      isDuplicateListing
        ? // @ts-expect-error - TS2339: Property 'catalogConflict' does not exist on type 'Icon'.
          Tabs.catalogConflict.mergeListing
        : // @ts-expect-error - TS2339: Property 'catalogConflict' does not exist on type 'Icon'.
          Tabs.catalogConflict.resolveConflict
    }/?sku=${encodeURIComponent(sku)}`;

    navigate(url);
  };

  return (
    <>
      &nbsp;
      {isMissingInfo && (
        <span onClick={redirectToMissingInfo} className="sk:cursor-pointer">
          <StatusBox
            status={
              isDuplicateListing ? 'Duplicate Listing' : 'Missing Information'
            }
            bgColor="#FF4218"
            minWidth="92px"
          />
        </span>
      )}
      {isDiscontinued && (
        <StatusBox
          status={
            isInactive
              ? 'Inactive'
              : state === ItemStates.component
                ? 'Component'
                : 'Discontinued'
          }
          minWidth="70px"
        />
      )}
    </>
  );
}

/**
 * The text box react-datepicker opens its calendar from. It clones this with
 * its own value and handlers, which all have to reach the `<input>`.
 */
const CalenderInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...rest }, ref) => (
  <span className="sk:group sk:relative sk:flex sk:max-w-[120px] sk:items-center">
    <input
      {...rest}
      ref={ref}
      className={cn(FIELD_INPUT, 'sk:pr-8!', className, 'sk:m-0!')}
    />
    <Calendar
      size={16}
      aria-hidden
      className="sk:pointer-events-none sk:absolute sk:right-2.5 sk:text-fg-tertiary sk:group-focus-within:text-fg"
    />
  </span>
));
CalenderInput.displayName = 'CalenderInput';

export function ExpirationDateInput({
  requiredError,
  value,
  defaultDate,
  onChange,
  disabled = false,
}) {
  return (
    <>
      <CenterRow className="sk:justify-between!">
        <MuteText className="sk:mr-auto! sk:text-left">
          Expiration Date
        </MuteText>
        <DatepickerContainer>
          <DatePicker
            dateFormat={DateFormat.datepicker}
            selected={value}
            openToDate={defaultDate}
            onChange={onChange}
            disabled={disabled}
            customInput={<CalenderInput />}
            minDate={new Date()}
          />
        </DatepickerContainer>
      </CenterRow>
      <div className="sk:flex! sk:justify-end!">
        {requiredError && <DangerText>Expiration Date is required</DangerText>}
      </div>
    </>
  );
}

/** Greys out its images while it carries `disabled-image`. */
export const NarfConatiner = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:relative sk:[&.disabled-image_img]:opacity-60 sk:[&.disabled-image_img]:grayscale',
        className,
      )}
    />
  ),
);
NarfConatiner.displayName = 'NarfConatiner';

export const NarfTag = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:absolute sk:top-1/2 sk:left-1/2 sk:-translate-x-1/2 sk:-translate-y-1/2',
        'sk:rounded-sm sk:bg-primary sk:px-2.5 sk:py-1 sk:text-xs sk:font-medium',
        'sk:text-white! sk:[&_span]:text-white!',
        className,
      )}
    />
  ),
);
NarfTag.displayName = 'NarfTag';

export const calculateTotalCartons = (
  isMultiple,
  isPOShipment,
  item,
  maxQty,
  isUnitView = false,
  isWSShipment = false,
) => {
  let ctns = 0;

  const aliasKey = isPOShipment || isWSShipment ? 'aliases' : 'variants';
  const attributeKey =
    isPOShipment || isWSShipment ? 'attributes' : 'attribute';
  const availableKey = isPOShipment ? 'qty' : 'available';

  if (isWSShipment) {
    return maxQty;
  }

  if (isMultiple) {
    item[aliasKey]?.forEach(alias => {
      if (isUnitView) {
        ctns += alias[availableKey];
      } else {
        let unitsPerCarton = alias[attributeKey]?.units_per_carton || 0;
        if (!unitsPerCarton && isWSShipment) {
          unitsPerCarton = 1;
        }

        ctns += Math.round(alias[availableKey] / unitsPerCarton);
      }
    });
  } else {
    return maxQty;
  }

  return ctns;
};

export const calculateTotalShipmentCartons = (
  isMultiple,
  isPOShipment,
  item,
  maxQty,
  isUnitView = false,
  isWSShipment = false,
) => {
  let ctns = 0;

  const aliasKey = isPOShipment || isWSShipment ? 'aliases' : 'variants';
  const attributeKey =
    isPOShipment || isWSShipment ? 'attributes' : 'attribute';
  const availableKey = isPOShipment ? 'qty' : 'available';

  if (isWSShipment) {
    return maxQty;
  }

  if (isMultiple) {
    item[aliasKey]?.forEach(alias => {
      if (isUnitView) {
        ctns += alias[availableKey];
      } else {
        let unitsPerCarton = alias[attributeKey]?.unitsPerCarton || 0;
        if (!unitsPerCarton && isWSShipment) {
          unitsPerCarton = 1;
        }

        ctns += Math.round(alias[availableKey] / unitsPerCarton);
      }
    });
  } else {
    return maxQty;
  }

  return ctns;
};

export const isMultiple = (item, isPOShipment) => {
  if (item.variants?.filter(x => x.attribute)?.length > 1) return true;
  if (isPOShipment) return item?.aliases?.length > 1;
};

export const calculateTotalUnits = (
  item,
  AttributeKey,
  aliasItemKey,
  updatedValue,
  isUnitView = false,
  isPOShipment,
) => {
  let qty = 0;

  if (isMultiple(item, isPOShipment)) {
    item[aliasItemKey]?.forEach(aliasItem => {
      if (isUnitView) {
        qty += Number(updatedValue[item._id]?.[aliasItem.alias] || 0);
      } else {
        qty +=
          Number(updatedValue[item._id]?.[aliasItem.alias] || 0) *
          (aliasItem[AttributeKey]?.units_per_carton || 0);
      }
    });
  }

  return qty || updatedValue[item._id]?.qty || 0;
};

export const calculateShipmentTotalUnits = (
  item: ShipmentCreateItem,
  AttributeKey,
  aliasItemKey,
  updatedValue,
  isUnitView = false,
  isPOShipment,
) => {
  let qty = 0;

  if (isMultiple(item, isPOShipment)) {
    item[aliasItemKey]?.forEach(aliasItem => {
      if (isUnitView) {
        qty += Number(updatedValue[item._id]?.[aliasItem.alias] || 0);
      } else {
        qty +=
          Number(updatedValue[item._id]?.[aliasItem.alias] || 0) *
          (aliasItem[AttributeKey]?.unitsPerCarton || 0);
      }
    });
  }
  return qty || updatedValue[item._id]?.qty || 0;
};

export type BlinkingBorderTableRowProps = CardTableRowProps & {
  blink?: boolean;
};

/**
 * Greys its text while it carries `disabled-row` (`black-text` opts out) and
 * shows its `.edit-row` controls on hover.
 */
export const BlinkingBorderTableRow = forwardRef<
  HTMLTableRowElement,
  BlinkingBorderTableRowProps
>(function BlinkingBorderTableRowPart({ blink, className, ...props }, ref) {
  return (
    <CardTableRow
      ref={ref}
      {...props}
      className={cn(
        'sk:transition-all sk:duration-300',
        blink && 'sk:bg-fill-secondary',
        'sk:[&.disabled-row_a]:text-fg-tertiary sk:[&.disabled-row_div]:text-fg-tertiary sk:[&.disabled-row_span]:text-fg-tertiary',
        'sk:[&_.black-text]:text-fg!',
        'sk:[&>td]:relative',
        'sk:[&_.edit-row]:pointer-events-none sk:[&_.edit-row]:opacity-0!',
        'sk:[&:hover_.edit-row]:pointer-events-auto sk:[&:hover_.edit-row]:opacity-100!',
        className,
      )}
    />
  );
});

export const readOnlyViewColumns = (isUnitView, completed) => {
  const arr = [
    {
      text: 'Product Information',
      width: 2,
      key: 'image',
    },
    {
      text: '',
      width: 4,
      key: 'info',
    },
    {
      text: isUnitView ? 'Units' : 'Quantity (Cartons)',
      key: 'ctn-quantity',
      width: 1,
    },
  ];

  if (!isUnitView) {
    arr.splice(2, 0, {
      text: 'Carton Configuration',
      width: 4,
      key: 'info',
    });

    arr.push({
      text: 'Quantity (Units)',
      key: 'quantity',
      width: 1,
    });
  }

  if (completed) {
    arr.push({
      text: 'Received',
      key: 'received',
      width: 1,
    });
  }

  return arr;
};

export const editViewCommonColumns = (
  isUnitView,
  isPOShipment,
  isCSShipment = false,
) => {
  const col = [
    {
      text: 'Product Information',
      width: 1,
      key: 'image',
    },
    {
      text: '',
      component: <div className="sk:mx-5! sk:px-5!" />,
      width: 2,
      key: 'info',
    },
    {
      text: isUnitView ? 'Preparation & Compliance' : 'Carton Configuration',
      key: 'carton-properties',
      textAlign: 'left',
      width: 3,
    },
    {
      text: 'Available Cartons at Origin (Days Left)',
      component: (
        <div className="sk:flex! sk:flex-col!">
          <span>Available {isUnitView ? 'Units' : 'Cartons'} at Origin</span>
          {isPOShipment ? <></> : <span className="sk:mt-1!">(Days Left)</span>}
        </div>
      ),
      key: 'available-carton',
      width: 1,
    },
    {
      text: isUnitView ? 'Units' : 'Carton',
      component: !isUnitView && (
        <div className="sk:flex! sk:flex-col!">
          <span>Carton</span>
          <span className="sk:mt-1!">Quantity</span>
        </div>
      ),
      key: 'quantity-ctn',
      width: 2,
    },
    {
      text: 'At Destination (Present/Expected)',
      component: (
        <div className="sk:flex! sk:flex-col!">
          <span className="sk:flex! sk:justify-center!">
            Units at Destination
            <FormHelpToolTip helpText={tooltipTexts.unitsAtDestination} />
          </span>
        </div>
      ),
      key: 'available',
      width: 2,
      sortKey: 'destination_available',
    },
  ];

  if (isCSShipment) {
    col.splice(3, 1);
  }

  if (!isUnitView) {
    col.push({
      text: 'Quantity (Units)',
      component: (
        <div className="sk:flex! sk:flex-col!">
          <span>Quantity</span>
          <span className="sk:mt-1!">(Units)</span>
        </div>
      ),
      key: 'quantity',
      width: 1,
    });
  }

  return col;
};

/** A shaded alias row under its item; its `.hide` controls show on hover. */
export const ShadedRow = forwardRef<
  HTMLTableRowElement,
  BlinkingBorderTableRowProps
>(function ShadedRowPart({ className, ...props }, ref) {
  return (
    <BlinkingBorderTableRow
      ref={ref}
      {...props}
      className={cn(
        'sk:[&:not(:hover)>td]:bg-primary-bg',
        'sk:[&_.hide]:opacity-0 sk:[&:hover_.hide]:opacity-100',
        className,
      )}
    />
  );
});
